/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSelector } from '@ngrx/store';
import { StepTask } from '@telespot/domain';

import { protocolFeature } from './protocol.reducer';
import { stepSelectors } from '../steps';
import { methodTypeSelectors } from '../method-types';
import { pipelineSelectors } from '../pipelines';
import { Category, Option, ProtocolScope, colors, defaultColor } from './protocol.state';
import { labelSelectors } from '../protocol-labels';

const { name, reducer, ...selectors } = protocolFeature;

function hexToRGBA(hex: string, opacity: number): string {
  hex = hex.replace('#', '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((hexChar) => hexChar + hexChar)
      .join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const selectMethodType = createSelector(
  selectors.selectMethodTypeId,
  methodTypeSelectors.selectEntities,
  (methodTypeId, methodTypes) => (methodTypeId ? methodTypes?.[methodTypeId] : undefined)
);

const selectPipelines = createSelector(
  selectMethodType,
  pipelineSelectors.selectEntities,
  stepSelectors.selectPipelineCategories,
  stepSelectors.selectCategories,
  stepSelectors.selectEntities,
  selectors.selectScope,
  (methodType, pipelines, pipelineCategories, categories, steps, scope) => {
    if (!methodType?.pipelineIds || !pipelines || !pipelineCategories) return [];

    return Object.keys(methodType.pipelineIds)
      .map((pipelineId) => ({
        ...pipelines[pipelineId],
        categories: [...pipelineCategories[pipelineId]],
      }))
      .filter((pipeline) => {
        return pipeline.categories.some((categoryId) => {
          if (categoryId === 'unlabeled') return false;

          const category = categories[categoryId];
          if (!category) return false;

          const step = steps[category.stepId];
          if (!step) return false;

          return step.generateFinding?.startsWith(scope);
        });
      });
  }
);

const selectCategories = createSelector(
  selectPipelines,
  stepSelectors.selectCategories,
  stepSelectors.selectIds,
  stepSelectors.selectEntities,
  labelSelectors.selectIds,
  labelSelectors.selectEntities,
  selectors.selectScope,
  (pipelines, categories, stepIds, steps, labelIds, labels, scope) => {
    const categoryRecord: Record<string, Category> = {};

    if (!pipelines?.length || !categories || !stepIds.length || !labelIds.length) return categoryRecord;

    for (const pipeline of pipelines) {
      for (const categoryId of pipeline.categories) {
        if (categoryId === 'unlabeled') continue;

        const category = categories[categoryId];
        const step = steps[category?.stepId];
        const label = labels[category?.id];
        const stepWithinScope = step?.generateFinding?.startsWith(scope);

        if (!step || !stepWithinScope) continue;

        categoryRecord[categoryId] = {
          ...category,
          scope,
          stepTask: step?.stepTask ?? StepTask.POSITION,
          name: label?.aliases?.['en']?.name,
        };
      }

      categoryRecord['unlabeled'] = {
        id: 'unlabeled',
        name: 'Unlabeled',
        stepId: 'unlabeled',
        scope: ProtocolScope.Asset,
        stepTask: StepTask.POSITION, // REVIEW: is this correct?
        options: ['unlabeled'],
      };
    }

    return categoryRecord;
  }
);

const selectOptions = createSelector(
  selectMethodType,
  selectCategories,
  stepSelectors.selectEntities,
  labelSelectors.selectEntities,
  selectors.selectScope,
  (methodType, categories, steps, labels, scope) => {
    const optionRecord: Record<string, Option> = {};

    if (!methodType || !categories || !steps || !labels) return optionRecord;

    for (const categoryId in categories) {
      if (categoryId === 'unlabeled') continue;

      const category = categories[categoryId];

      for (const { optionId, index } of category.options.map((optionId, index) => ({ optionId, index }))) {
        const step = steps[category.stepId];
        const label = labels[optionId];
        const config = methodType.labelConfig[optionId];
        const stepWithinScope = step?.generateFinding?.startsWith(scope);

        if (!label || !step || !stepWithinScope) continue;

        const color = config?.color ?? colors[index % colors.length] ?? defaultColor;

        optionRecord[optionId] = {
          ...labels[optionId],
          name: label.aliases['en']?.name, // FIX: provide all language names
          color: hexToRGBA(color, 1),
          threshold: Number(step?.thresholds?.[optionId]?.['thrdisp'] ?? 0),
        };
      }
    }

    optionRecord['unlabeled'] = {
      id: 'unlabeled',
      name: 'Unlabeled',
      color: 'black',
      threshold: 0,
    };

    return optionRecord;
  }
);

const selectLoading = createSelector(
  stepSelectors.selectLoading,
  methodTypeSelectors.selectLoading,
  pipelineSelectors.selectLoading,
  labelSelectors.selectLoading,
  (stepLoading, methodTypeLoading, pipelineLoading, labelLoading) =>
    stepLoading || methodTypeLoading || pipelineLoading || labelLoading
);

const selectError = createSelector(
  stepSelectors.selectError,
  methodTypeSelectors.selectError,
  pipelineSelectors.selectError,
  labelSelectors.selectError,
  (stepError, methodTypeError, pipelineError, labelError) => stepError || methodTypeError || pipelineError || labelError
);

const selectTaggingTasks = createSelector(selectCategories, (categories) => {
  const taggingCategories: Record<string, Category> = {};

  const categoryIds = Object.keys(categories ?? {});

  if (!categoryIds.length) return taggingCategories;

  for (const categoryId of categoryIds) {
    const isTaggingTask = [StepTask.POSITION, StepTask.ROIDETECTION, StepTask.ROITRACKING].includes(
      categories[categoryId]?.stepTask
    );

    if (!isTaggingTask) continue;

    taggingCategories[categoryId] = categories[categoryId];
  }

  taggingCategories['unlabeled'] = {
    id: 'unlabeled',
    name: 'Unlabeled',
    stepId: 'unlabeled',
    scope: ProtocolScope.Asset,
    stepTask: StepTask.POSITION, // REVIEW: is this correct?
    options: ['unlabeled'],
  };

  return taggingCategories;
});

const selectHasTaggingTasks = createSelector(
  selectTaggingTasks,
  (taggingTasks) => Object.keys(taggingTasks).length > 0
);

const selectSegmentationTasks = createSelector(selectCategories, (categories) => {
  const segmentationCategories: Record<string, Category> = {};

  const categoryIds = Object.keys(categories ?? {});

  if (!categoryIds.length) return segmentationCategories;

  for (const categoryId of categoryIds) {
    const isSegmentationTask = categories[categoryId]?.stepTask === StepTask.SEGMENTATION;

    if (!isSegmentationTask) continue;

    segmentationCategories[categoryId] = categories[categoryId];
  }

  return segmentationCategories;
});

const selectHasSegmentationTasks = createSelector(
  selectSegmentationTasks,
  (segmentationTasks) => Object.keys(segmentationTasks).length > 0
);

const selectTaggingPipelines = createSelector(selectTaggingTasks, selectPipelines, (taggingTasks, pipelines) => {
  const taggingTaskIdSet = new Map(Object.entries(taggingTasks ?? {}));

  return pipelines
    .map((pipeline) => {
      const filteredCategories = (pipeline.categories ?? []).filter(
        (categoryId) => taggingTaskIdSet.has(categoryId) && taggingTasks[categoryId]?.scope === ProtocolScope.Asset
      );

      if (filteredCategories.length === 0) return null;

      return { ...pipeline, categories: [...filteredCategories, 'unlabeled'] };
    })
    .filter(Boolean);
});

const selectAcquisitionFields = createSelector(selectMethodType, (methodType) => methodType?.acquisitionFields || []);

export const protocolSelectors = {
  ...selectors,
  selectMethodType,
  selectPipelines,
  selectCategories,
  selectOptions,
  selectLoading,
  selectError,
  selectHasTaggingTasks,
  selectHasSegmentationTasks,
  selectTaggingTasks,
  selectSegmentationTasks,
  selectTaggingPipelines,
  selectAcquisitionFields,
};
